/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */

import {MouseEvent} from 'react';
import isboolean from 'lodash.isboolean';
import isEmpty from 'lodash.isempty';
import omit from 'lodash.omit';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  BulkSettingFieldValues,
  Device,
  Devices,
  GenericObject,
  NonFlightModeType,
  SCIDeviceType,
  SCIField,
  SCIPendingSettings
} from '@ion/core/src/types/entities';
import {getUniqueArray} from '@ion/core/src/utils/ArrayUtil';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import {getDefaultFieldsObj} from './SCISettingFieldMapping';
import {Permission} from './state';

export const sensorLoggingPeriodList = [
  translate('admin-devices:view.sciSettings.sensorsOptions.combined'),
  translate('admin-devices:view.sciSettings.sensorsOptions.logOnly'),
  translate('admin-devices:view.sciSettings.sensorsOptions.logAndSendReport'),
  translate('admin-devices:view.sciSettings.sensorsOptions.logAndSendReportWithBackoff')
];

export const nonFlightModeTypes = [
  SCIDeviceType.SC1004,
  SCIDeviceType.SC1004V,
  SCIDeviceType.SC1204,
  SCIDeviceType.SC1204S,
  SCIDeviceType.SC1204V,
  NonFlightModeType.SC1205V
];

const advanceTabs = ['controls', 'tagDetection', 'sensors', 'stationary', 'moving', 'ignition'];

export const temperatureLoggingFields = ['lightLevelAlert', 'temperatureAlert', 'impactAlert', 'tipAlert', 'extremeTemperatureLogging'];

export const maxLigthLevelFields = ['maxLightLevel', 'maxTemperature', 'maxImpact', 'maxTip'];

export const MIN_ACCURACY_VALUE    = 0;
export const MAX_ACCURACY_VALUE    = 6;
export const MAX_DEVICE_TO_DISPLAY = 25;

/**
 * SCI Device setting APP KEY
 */
export const SCIAPPKEY = 'e7d961ff-5943-4a2a-9f95-bd8598256d25';

const SC1004v = 'SC1004v';

//Checks the pending status of specific field
export const pendingValueCheck = (pendingVal, key=null) => {
  let showPendingVal;
  if (typeof pendingVal === 'boolean') {
    showPendingVal = pendingVal ? 'ON' : 'OFF';
    return showPendingVal;
  }
  if (key === 'zMicroRxPeriod' || key === 'zMicroRxTime') {
    return pendingVal;
  }
  return reportingIntervalInitialVal(key, pendingVal);
};

export const reportingIntervalInitialVal = (key, value, bulkSCI = false) => {
  if (!isNaN(value)) {
    if (bulkSCI && value === '') {
      return value;
    }
    if (key === 'stationaryReportingDelay' && value === 0) {
      return 'No Delay';
    }
    if (value <= 300) {
      return '5mins';
    }
    if (value > 300 && value <= 600) {
      return '10mins';
    }
    if (value > 600 && value <= 900) {
      return '15mins';
    }
    if (value > 900 && value <= 1800) {
      return '30mins';
    }
    if (value > 1800 && value <= 3600) {
      return '1hour';
    }
    if (value > 3600 && value <= 7200) {
      return '2hours';
    }
    if (value > 7200 && value <= 14400) {
      return '4hours';
    }
    if (value > 14400 && value <= 28800) {
      return '8hours';
    }
    if (value > 28800 && value <= 43200) {
      return '12hours';
    }
    if (value > 43200 && value <= 57600) {
      return '16hours';
    }
    if (value > 57600 && value <= 86400) {
      return '24hours';
    }
  }
  // To change Dropdown value
  if (typeof value === 'string') {
    return value;
  }
};

export const availableModels = [
  {
    indx : 0,
    name : 'SC1004'
  },
  {
    indx : 1,
    name : 'SC1004V'
  },
  {
    indx : 2,
    name : 'SC1100'
  },
  {
    indx : 3,
    name : 'SC1204'
  },
  {
    indx : 4,
    name : 'SC1204S'
  },
  {
    indx : 5,
    name : 'SC1204V'
  },
  {
    indx : 6,
    name : 'SC1300'
  },
  {
    indx : 7,
    name : 'STAG09XT'
  }
];

const loggingIntervalConversions = {
  0          : 0,
  'No Delay' : 0,
  '5 mins'   : 300,
  '10 mins'  : 600,
  '15 mins'  : 900,
  '30 mins'  : 1800,
  '1 hour'   : 3600,
  '2 hours'  : 7200,
  '4 hours'  : 14400,
  '8 hours'  : 28800,
  '12 hours' : 43200,
  '16 hours' : 57600,
  '24 hours' : 86400
};

export const defaultReceiveInterval = {
  'Only When Reporting' : 0,
  'Always On'           : 1,
  Timed                 : 60
};

export const defaultReceiveTime = {
  'Only When Reporting' : 30000,
  'Always On'           : 0,
  Timed                 : 30000
};

export const SCITypeNames = {
  SC1004V : '(SC1004V OR SC1004v)',
  SC1100  : '(SC1100 OR SC1102)',
  SC1300  : '(SC1300 OR SC1302)'
};

export const path = 'admin-devices:view.bulkSciSetting';
export const DEFAULT_QUERY_STRING_FOR_UPDATE = 'typeName: (SC* OR *tag* OR *Tag* OR *TAG*)';

export const convertLoggingPeriodInterval = (selectedOpt: MouseEvent<HTMLInputElement> | string): number => {
  return loggingIntervalConversions[selectedOpt.toString()];
};

export const getActiveTabIndex = (activeTab: string): number => {
  return advanceTabs.indexOf(activeTab);
};

export const getSensorLoggingPeriodTabIndex = (activeTab: MouseEvent<HTMLInputElement>): number => {
  const allSensorFields = [...sensorLoggingPeriodList];
  return allSensorFields.indexOf(activeTab);
};

export const getAddedNetwork = (a, b) => {
  const first  = a?.filter(wifiNetworkComparer(b));
  const second = b?.filter(wifiNetworkComparer(a));
  const all = first.concat(second);
  return JSON.stringify({
    networks : all.length ? all : undefined
  });
};

const wifiNetworkComparer = (otherArray = []) => {
  return ({ssid, pass}) => (
    otherArray.filter(other => (ssid === other.ssid && pass === other.pass)).length === 0
  );
};

/**
 * @deprecated - iOn Monorepo Applications are ONLY to determine Access to Features
 * via the Applications Features. References to `Permissions` is **An Anti-Pattern!**
 * and are **NOT TO BE USED!!!**. For additional information and details, please see:
 * https://calampcorp.atlassian.net/browse/IMO-174.
 */
export const hasPermission = (permissions: Permission, action: string, resource: string) => {
  const permissionsMap = getPermissionMap(permissions);
  return permissionsMap[permissionExpression(action, resource)] === true;
};

/**
 * @deprecated - iOn Monorepo Applications are ONLY to determine Access to Features
 * via the Applications Features. References to `Permissions` is **An Anti-Pattern!**
 * and are **NOT TO BE USED!!!**. For additional information and details, please see:
 * https://calampcorp.atlassian.net/browse/IMO-174.
 */
export const getPermissionMap = (permissions: Permission) => {
  const permissionsMap = {};
  permissions?.forEach(({permission}) => {
    const expression = permissionExpression(permission.action, permission.resource);
    permissionsMap[expression] = true;
  });

  return permissionsMap;
};

/**
 * @deprecated - iOn Monorepo Applications are ONLY to determine Access to Features
 * via the Applications Features. References to `Permissions` is **An Anti-Pattern!**
 * and are **NOT TO BE USED!!!**. For additional information and details, please see:
 * https://calampcorp.atlassian.net/browse/IMO-174.
 */
export const permissionExpression = (action, resource) => {
  return (resource + ':' + action).toLowerCase();
};

export const checkSCIDeviceSubscription = (applications) => {
  const application = applications?.find((app) => app.href.split('/').reverse()[0] === SCIAPPKEY);
  return !!application;
};

export const sensorPendingFields = ({
  temperatureLogging,
  sensorLoggingPeriod,
  syncSensorLog,
  syncSensorLogTempLimit,
  sensorEventMode
}) => {
  const pendingFields = [];
  if (temperatureLogging) {
    pendingFields.push(`${translate('admin-devices:view.sciSettings.pending')}${temperatureLogging
      ? translate('admin-devices:view.sciSettings.on')
      : translate('admin-devices:view.sciSettings.off')}, `
    );
  }
  if (sensorLoggingPeriod) {
    pendingFields.push(`${translate('admin-devices:view.sciSettings.sensorLogPeriod')}: ${sensorLoggingPeriod}, `);
  }
  if (syncSensorLog) {
    pendingFields.push(`${translate('admin-devices:view.sciSettings.syncSensorLog')}: ${sensorLoggingPeriod
      ? translate('admin-devices:view.sciSettings.on')
      : translate('admin-devices:view.sciSettings.off')}, `
    );
  }
  if (syncSensorLogTempLimit) {
    pendingFields.push(`${translate('admin-devices:view.sciSettings.syncSensorTemp')}: ${syncSensorLogTempLimit}, `);
  }
  if (sensorEventMode) {
    pendingFields.push(`${translate('admin-devices:view.sciSettings.sensorEventMode')}: ${sensorEventMode}, `);
  }
  return pendingFields;
};

export const getAllSCIDevices = (devices) => {
  const nonSciDevices = [];
  const sciDevices    = {};
  for (const type in SCIDeviceType) {
    sciDevices[type] = [];
  }
  devices.forEach(device => {
    const type = device?.typeName?.toUpperCase();
    (sciDevices[type])?.push(device);
    if (!Object.values(SCIDeviceType).includes(type)) {
      nonSciDevices.push(device);
    }
  });
  let mergedSCIDevices = omit(mergeTypes(sciDevices), ['SC1102', 'SC1302']);
  mergedSCIDevices = Object.values(mergedSCIDevices)
    .filter((eachType: Devices) => eachType.length > 0);
  let selectedAllTypes = [];
  const typeDevicesLength = [];
  mergedSCIDevices.map((type) => {
    if (type.length > 0) {
      selectedAllTypes.push(type[0].typeName);
      typeDevicesLength.push(type.length);
    }
  });
  selectedAllTypes = getUniqueArray(selectedAllTypes);
  return {sciDevices: mergedSCIDevices, nonSciDevices, selectedAllTypes, typeDevicesLength};
};

const mergeTypes = (sciDevices) => {
  const mergedTypes = {
    ...sciDevices,
    SC1204 : sciDevices[SCIDeviceType.SC1204].concat(sciDevices[SCIDeviceType.SC1102]),
    SC1300 : sciDevices[SCIDeviceType.SC1300].concat(sciDevices[SCIDeviceType.SC1302])
  };
  return mergedTypes;
};

export const getBulkSelectedValue = (key: string): boolean => {
  if (key !== BulkSettingFieldValues.default) {
    return key === BulkSettingFieldValues.on;
  }
  return null;
};

export const isSensorField = (key): boolean => (
  temperatureLoggingFields.includes(key) ||
    key === 'temperatureLogging' ||
    key === 'syncSensorLog'
);

export const modifiedSettingCheck = (changedSettingFields: GenericObject): boolean => {
  const isWifiFields = safeJSONParse(changedSettingFields?.wifiNetworks);
  return !(isEmpty(changedSettingFields) && isEmpty(isWifiFields?.networks));
};

export const getValueLabel = (key, value) => {
  let label = value;
  if (isboolean(value)) {
    label = !value ? BulkSettingFieldValues.on : BulkSettingFieldValues.off;
  } else if (key === 'wifiNetworks') {
    const isWifiFields = safeJSONParse(value)?.networks?.length;
    label = isWifiFields;
  }
  return label;
};

export const getUpdatedValuesForChipDisplay = (
  changedSettingFields: SCIPendingSettings,
  selectedDevicetype: string
): SCIField[] => {
  if (!isEmpty(changedSettingFields)) {
    const [customInitialFields,
      reportAndLogsInitialFields,
      sensorsInitialFields,
      advanceInitialFields
    ] = getDefaultFieldsObj({}, selectedDevicetype);
    const allFields = [];
    allFields.push(
      ...customInitialFields,
      ...sensorsInitialFields,
      ...reportAndLogsInitialFields.flat(1),
      ...advanceInitialFields.flat(1));
    const fields = [];
    for (const [key, value] of Object.entries(changedSettingFields)) {
      let text = key;
      const displayValue = getValueLabel(key, value);
      allFields.forEach(eachField => {
        if (Object.keys(eachField).includes(key)) {
          text = eachField.label;
        }
      });
      const label = `${text} = ${displayValue}`;
      fields.push({label, key});
    }
    return fields;
  }
  return [];
};

export const getDeviceIdsList = (devices: Devices) => {
  const deviceIds = devices?.reduce((ids, device) => {
    ids.push(entityId(device?._self?.href));
    return ids;
  }, []);
  return deviceIds;
};

export const checkSCIDevice = (device: Device): boolean => {
  const allTypes: string[] = Object.values(SCIDeviceType);
  return allTypes?.includes(device?.typeName) || device?.typeName === SC1004v;
};

export const getDisableGPSValue = (value): boolean => !value;
